import { FC } from "react";
import { Applicant } from "types/application";
import { CriminalReport, VerificationReport } from "types/landlord/reports";
import { ReportHeader } from "../../components/ReportHeader";
import api from "api/landlord/applications";
import { ReportAlertMessage } from "./components/ReportAlertMessage";
import { useQuery } from "@tanstack/react-query";
import { Loader } from "shared/components/Loader";
import { Section } from "views/applications-id-report/components/Section";
import { Stat } from "views/applications-id-report/components/Stat";
import { ReportException } from "views/applications-id-report/components/ReportException";
import {
  DataVendors,
  VerificationNames,
  VerificationStatus
} from "shared/types";
import { ReportCompletedBanner } from "views/applications-id-report/components/ReportCompletedBanner";
import { FinishedWithWarningsBanner } from "views/applications-id-report/components/FinishedWithWarningsBanner";
import { getVerificationQueryKeys } from "views/applications-id-report/utils/getVerificationQueryKeys";
import { ReportViewApproval } from "views/applications-id-report/slices/CriminalReportSlice/components/ReportViewApproval";
import { PrintReady } from "views/applications-id-report/providers/PrintProvider";
import { ApplicantSettings } from "shared/types";
import { CLEARA_CATEGORIZATION_SCHEMA_VERSION } from "views/applications-id-report/utils/constants";
import { GroupedCriminalRecords } from "./components/GroupedCriminalRecords";
import { CriminalRecords } from "./components/CriminalRecords";

type CriminalReportSliceProps = {
  applicant: Applicant;
  verificationId: number;
  screeningCriteria?: ApplicantSettings;
};

export const CriminalReportSlice: FC<CriminalReportSliceProps> = ({
  applicant,
  verificationId,
  screeningCriteria
}) => {
  const verification = useQuery<VerificationReport<CriminalReport>>({
    queryKey: getVerificationQueryKeys({
      reportName: VerificationNames.CriminalReport,
      applicationId: applicant.application_id,
      applicantId: applicant.id,
      verificationId
    }),
    queryFn: () =>
      api
        .getApplicantVerification(
          applicant.application_id,
          applicant.id,
          verificationId
        )
        .then((res) => res.verification)
  });

  const { report, status, vendor, schema } = verification.data ?? {};
  const { results = [], criteria, grouped_results = {} } = report ?? {};

  if (verification.isLoading)
    return (
      <>
        <ReportHeader
          name={VerificationNames.CriminalReport}
          title="Criminal report"
          reportDate=""
          verificationId={verificationId}
        />
        <Loader />
      </>
    );

  if (
    !report ||
    Object.keys(report).length === 0 ||
    status === VerificationStatus.Failed
  )
    return (
      <ReportException
        title="Criminal report"
        verificationId={verificationId}
      />
    );

  if (report.require_approval)
    return (
      <ReportViewApproval
        reportDate={verification.data?.updated_at}
        verificationId={verificationId}
        refetch={verification.refetch}
        applicantId={applicant.id}
        applicationId={applicant.application_id}
      />
    );

  const statHeight = criteria ? "80px" : "auto";

  const { summary } = report;
  const { version } = schema || {};

  const hasClearaCategorization =
    vendor === DataVendors.Cleara &&
    version &&
    version >= CLEARA_CATEGORIZATION_SCHEMA_VERSION;

  return (
    <div className="flex-1">
      <ReportHeader
        title="Criminal report"
        reportDate={verification.data?.updated_at}
        verificationId={verificationId}
      />

      <PrintReady verificationId={verificationId} />

      <ReportAlertMessage title="Disclaimer" message={report.disclaimer} />
      <ReportAlertMessage
        title="Consumer statement"
        message={report.consumer_statement}
      />

      <Section sectionTitle="Criminal report summary" maxWidth="none">
        {verification.data?.completed_at ? (
          <ReportCompletedBanner
            verificationName={VerificationNames.CriminalReport}
            completedAt={verification.data.completed_at}
            reportId={String(verificationId)}
            vendor={verification.data.vendor}
          />
        ) : null}

        {verification.data?.status ===
          VerificationStatus.FinishedWithWarnings && report.error_message ? (
          <FinishedWithWarningsBanner msg={report.error_message} />
        ) : null}

        <div className="flex flex-wrap items-center gap-4">
          {hasClearaCategorization ? (
            <>
              <Stat
                title="Felony record"
                value={String(summary?.total_felony_records)}
                width="240px"
                height={statHeight}
              />
              <Stat
                title="Misdemeanor record"
                value={String(summary?.total_misdemeanor_records)}
                width="240px"
                height={statHeight}
              />
              <Stat
                title="Other criminal record"
                value={String(summary?.total_other_criminal_records)}
                width="240px"
                height={statHeight}
              />
            </>
          ) : (
            <>
              <Stat
                title="Criminal record"
                value={String(summary?.total_criminal_records)}
                width="240px"
                height={statHeight}
              />
              <Stat
                title="Criminal Traffic"
                value={String(summary?.total_traffic_records)}
                width="240px"
                height={statHeight}
              />
            </>
          )}

          <Stat
            title="National sex offender"
            value={String(summary?.total_sex_offender_records)}
            width="240px"
            hasCriteria={!!criteria?.sex_offender_check}
            criteriaValue={criteria?.sex_offender_check ? 0 : ""}
            criteriaSuccess={(summary?.total_sex_offender_records || 0) <= 0}
            height={statHeight}
            criteriaImportance={
              screeningCriteria?.sex_offender_check?.importance
            }
          />
          <Stat
            title="Potential OFAC match"
            value={String(summary?.total_ofac_records)}
            width="240px"
            height={statHeight}
          />
        </div>
      </Section>

      {hasClearaCategorization ? (
        <div className="py-2">
          <GroupedCriminalRecords
            verificationId={verificationId}
            results={grouped_results.felony_records || []}
            total={summary?.total_felony_records || 0}
            title="Felony records"
          />

          <GroupedCriminalRecords
            verificationId={verificationId}
            results={grouped_results.misdemeanor_records || []}
            total={summary?.total_misdemeanor_records || 0}
            title="Misdemeanor records"
          />

          <GroupedCriminalRecords
            verificationId={verificationId}
            results={grouped_results.other_criminal_records || []}
            total={summary?.total_other_criminal_records || 0}
            title="Other criminal records"
          />

          <GroupedCriminalRecords
            verificationId={verificationId}
            results={grouped_results.sex_offender_records || []}
            total={summary?.total_sex_offender_records || 0}
            title="National sex offender records"
          />

          <GroupedCriminalRecords
            verificationId={verificationId}
            results={grouped_results.ofac_records || []}
            total={summary?.total_ofac_records || 0}
            title="Potential OFAC match records"
          />
        </div>
      ) : (
        <CriminalRecords verificationId={verificationId} results={results} />
      )}
    </div>
  );
};
